// Pure round-timing helpers shared between the RoundsEditor (the per-event board
// controls) and the header's live-timer indicator. Times are 'HH:MM' or
// 'HH:MM:SS' strings stored on meta.rounds; everything here is resolved against
// the caller's `now` so the same clock drives the board and the header chip.
import { eventImageUrl } from '../calendar/calendar.lib';
import type { CalendarEvent, EventRound } from '../calendar/calendar.schema';

export const pad = (n: number) => String(n).padStart(2, '0');

// 'HH:MM[:SS]' → today's Date at that wall-clock time (seconds optional).
export const todayAt = (time: string, now: Date): Date => {
    const [h, m, s = 0] = time.split(':').map(Number);
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, s);
};

export type RoundStatus = 'upcoming' | 'live' | 'done';

// A round only runs once it's been explicitly started — marked by a seconds-
// precision start ('HH:MM:SS', stamped by the Start action). Scheduled picks are
// minute-resolution ('HH:MM') and never go live on their own: a round whose set
// start is already in the past must be started by hand. So the clock can carry a
// *started* round through to done, but it never starts one.
export const isStarted = (round: EventRound): boolean => round.start.split(':').length === 3;

export function roundStatus(round: EventRound, now: Date): RoundStatus {
    if (!round.start || !isStarted(round)) return 'upcoming';
    if (round.end && now.getTime() >= todayAt(round.end, now).getTime()) return 'done';
    return 'live';
}

export function formatDuration(ms: number): string {
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(s / 3600);
    const rest = `${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
    return h > 0 ? `${h}:${rest}` : rest;
}

export type ActiveTimer = {
    eventId: number;
    title: string;
    location?: string;
    // Event logo (falls back to the calendar background), thumb size — shown next
    // to the timer in the header. null when the event has no image.
    logoUrl: string | null;
    // ms left until the round ends, or null for an open-ended live round (no end
    // set) — those count up instead, so `display` carries the elapsed time.
    remainingMs: number | null;
    display: string;
};

// Every currently-live round across the given events, soonest-ending first
// (open-ended rounds sort last). Drives the header live-timer chips.
export function activeTimers(events: CalendarEvent[], now: Date): ActiveTimer[] {
    const timers: ActiveTimer[] = [];
    for (const ev of events) {
        const logoUrl =
            eventImageUrl(ev.imageLogo, 'thumb') ?? eventImageUrl(ev.imageCalendarBackground, 'thumb');
        for (const round of ev.meta?.rounds ?? []) {
            if (roundStatus(round, now) !== 'live') continue;
            const remainingMs = round.end ? todayAt(round.end, now).getTime() - now.getTime() : null;
            const display =
                remainingMs !== null
                    ? formatDuration(remainingMs)
                    : formatDuration(now.getTime() - todayAt(round.start, now).getTime());
            timers.push({ eventId: ev.id, title: ev.title, location: ev.location, logoUrl, remainingMs, display });
        }
    }
    return timers.sort((a, b) => (a.remainingMs ?? Infinity) - (b.remainingMs ?? Infinity));
}
